/**
 * Beat 2's authored content: where the worker walks, what she can reach, and
 * what he can see.
 *
 * Numbers, not behaviour. `Worker` knows how to follow a route and
 * `StealthSystem` knows how to look; this file is the only place that says
 * *which* route and *whose* eyes, and it is deliberately readable as staging
 * rather than as code. Everything here is a decision that could be different and
 * would change how the beat plays; nothing here is a mechanism.
 *
 * It is separate from `farrowing.ts` for the same reason `bornBay.ts` is: the
 * hall is a room that will host seven beats, and a beat's staging must not
 * accumulate inside it.
 */

import { HALL, BAY_PITCH, WALKWAY_DOOR, penGateMouths } from '@/world/farrowing';
import { BOOTS_DOOR_AT, BOOTS_PARK, BOOTS_WALKWAY_X, bornAllowedArea, type BayArea } from '@/world/bornBay';
import { ROSALIE_TUNING } from '@/physics/character';
import type { WorkerRoute } from '@/systems/worker';
import type { Watcher } from '@/systems/stealth';

/** Which row of pens she was born in. Beat 1's bay is on it. */
const SIDE = -1;

/** Bay centres along Z, near end first. Same module the hall is built on. */
function bayCentre(index: number): number {
  return -HALL.halfLength + BAY_PITCH / 2 + index * BAY_PITCH;
}

/** Her bay: the near-end one, closest to the door. Beat 1 happened in it. */
export const LATCH_BAY_Z = bayCentre(0);

/** The far trough. The other end of the hall, and the reason the beat works. */
export const FAR_TROUGH_Z = bayCentre(7);

/**
 * How long he stands at the far trough. **This is the beat.**
 *
 * Spec §7 says forty seconds and forty seconds is what it is. Long enough to
 * cross twenty metres of hall at a creep and still have time to be wrong once;
 * short enough that standing still and waiting for a better moment costs
 * something. It is the only number in this file that should be changed with a
 * stopwatch and a playtest rather than by argument.
 */
export const FAR_TROUGH_DWELL = 40;

/** How long he spends at each of the near troughs. Enough to read as work. */
const STOP_DWELL = 5;

/**
 * The bearing that faces into the pen row, for a worker standing on the walkway.
 *
 * Stealth's convention: 0 faces +Z, increasing toward +X. Facing the left-hand
 * row means facing -X, which is -π/2 — and it matters far more than it looks,
 * because a worker facing into a pen has his cone pointed at the pigs and not
 * down the hall she has to cross. **The safety of the window is his neck, not
 * his distance.**
 */
const FACING_INTO_PENS = SIDE < 0 ? -Math.PI / 2 : Math.PI / 2;

/**
 * The feeding round.
 *
 * Three stops on the way up and then the far trough, rather than a stop at every
 * one of the eight bays. Eight stops is what a real round is and it is 2 minutes
 * 18 of a player watching a man walk; three is enough for the rhythm — *stop,
 * work, move on* — to be a rhythm rather than an event. Spec §7 asks for the
 * pattern to become legible, and a pattern needs three terms.
 *
 * The first and last waypoints are the DOORWAY, not the walkway: he comes out
 * of the vestibule through the walkway door, then steps across onto the
 * walkway line at -0.46 (beat 1 eases the same move; here it is two
 * waypoints). The door opens for him and is left ajar behind him, which is
 * how beat 2 ends (`WALKWAY_DOOR_AJAR`).
 *
 * `park` is beat 1's, in the vestibule, and `returnVia` is the doorway
 * waypoint: when she is spotted he abandons the round and goes back to `park`
 * from wherever he is, and a straight line home from the near bay crossed the
 * doorway 0.29 m from its left jamb, inside his 0.32 m half-width. Through the
 * doorway point he passes it square. `walkway-door.spec.ts` walks the round
 * and the way home from every waypoint. Until the revision the park was
 * outdoors and the doorway was a curtain at x -1.9, so every round began by
 * walking through the end wall and across her pen.
 */
export function latchWorkerRoute(): WorkerRoute {
  return {
    park: { x: BOOTS_PARK.x, z: BOOTS_PARK.z },
    returnVia: { x: BOOTS_DOOR_AT.x, z: BOOTS_DOOR_AT.z },
    // A man walking a hall he has walked ten thousand times. Beat 1's boots move
    // at exactly this and the two must not disagree — he is the same man.
    speed: 0.62,
    // Half a second to turn ninety degrees. Slower than a person and it is meant
    // to be: the turn at the far trough is the tell that the window has closed,
    // and a tell that happens inside one frame is not a tell.
    turnRate: 3.1,
    stride: 0.62,
    restBetweenRounds: 8,
    waypoints: [
      { x: BOOTS_DOOR_AT.x, z: BOOTS_DOOR_AT.z, dwell: 0 },
      { x: BOOTS_WALKWAY_X, z: bayCentre(0), dwell: STOP_DWELL, facing: FACING_INTO_PENS },
      { x: BOOTS_WALKWAY_X, z: bayCentre(2), dwell: STOP_DWELL, facing: FACING_INTO_PENS },
      { x: BOOTS_WALKWAY_X, z: bayCentre(4), dwell: STOP_DWELL, facing: FACING_INTO_PENS },
      { x: BOOTS_WALKWAY_X, z: FAR_TROUGH_Z, dwell: FAR_TROUGH_DWELL, facing: FACING_INTO_PENS },
      { x: BOOTS_DOOR_AT.x, z: BOOTS_DOOR_AT.z, dwell: 0 },
    ],
  };
}

/** The index of the far trough in the route above. The window is his dwell there. */
export const FAR_TROUGH_WAYPOINT = 4;

/**
 * The worker's eyes.
 *
 * A **narrow** cone and a **short** range, and both are generosity rather than
 * realism. A man can see most of a lit hall; this one sees 8 m of it through
 * 90°, because spec §5.1 requires every detection to be explainable after the
 * fact and a cone that reaches the far wall makes "he saw me" indistinguishable
 * from "he was facing roughly that way". The player has to be able to look at
 * where he is standing and know whether they are in it.
 *
 * `eyeHeight` is 1.55 — a real one. She is 0.32 and the ray goes from his eye to
 * her back at 0.2, which is what makes the 0.55 m kerb do work in the geometry
 * as well as in the fiction.
 */
export function latchWatcher(position: { x: number; z: number }, facing: number): Watcher {
  return {
    id: 'latch.worker',
    position: { x: position.x, y: 0, z: position.z },
    facing,
    coneHalfAngle: Math.PI / 4,
    range: 8,
    eyeHeight: 1.55,
  };
}

// The exit used to be declared here as `DOOR_AT`, a point in her own pen.
// It is the walkway door now: `WALKWAY_DOOR_OUT_Z` in `farrowing.ts`.

/**
 * Where Rosalie may be in beat 2 once the gate is open and the door ajar:
 * beat 1's bay (`bornAllowedArea`), the gate's mouth onto the walkway, the
 * walkway, the doorway and the vestibule behind it. Regions are for her
 * capsule centre, as beat 1's are.
 *
 * The plan asked for this per beat state (`docs/beat-1-revision-plan.md`, A),
 * and it waited for the door because the doorway is the region that matters:
 * `walkway-door.spec.ts` floods the hall in this state and fails on anything
 * reached outside it, which is what a hole into another pen, or a door that
 * lets her past its hinge side, would look like.
 */
export function latchAllowedArea(): BayArea {
  const r = ROSALIE_TUNING.radius;
  const bay = bornAllowedArea();
  const mouth = penGateMouths()[0];
  if (mouth === undefined) throw new Error('latchAllowedArea: the hall declares no pen gate mouth');
  const kerbInner = HALL.walkway + 0.12;
  const walkway = HALL.walkway - r;
  const wallOut = -HALL.halfLength - HALL.wallThickness;
  return {
    regions: [
      ...bay.regions,
      {
        name: 'gate opening',
        xMin: mouth.side < 0 ? -kerbInner - r : walkway,
        xMax: mouth.side < 0 ? -walkway : kerbInner + r,
        zMin: mouth.z - mouth.width / 2,
        zMax: mouth.z + mouth.width / 2,
      },
      { name: 'walkway', xMin: -walkway, xMax: walkway, zMin: -HALL.halfLength + r, zMax: HALL.halfLength - r },
      // The wall's whole opening, frame included. The jambs and the leaf are
      // solids, so the fill keeps her a radius off them; what this region
      // adds is the floor in front of the opening, where the wall face that
      // keeps the walkway region a radius back is not there.
      {
        name: 'doorway',
        xMin: -WALKWAY_DOOR.halfWidth,
        xMax: WALKWAY_DOOR.halfWidth,
        zMin: wallOut,
        zMax: -HALL.halfLength + r,
      },
      {
        name: 'vestibule',
        xMin: -WALKWAY_DOOR.vestibuleHalfX + r,
        xMax: WALKWAY_DOOR.vestibuleHalfX - r,
        zMin: wallOut - WALKWAY_DOOR.vestibuleDepth + r,
        zMax: wallOut,
      },
    ],
    keepOut: bay.keepOut,
  };
}
